import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getLead,
  updateLead,
  getLeadNotes,
  createLeadNote,
  updateLeadNote,
  deleteLeadNote,
  getLeadHistory,
  getStatuses,
  getSellers,
  toggleLeadMarker,
} from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { formatDate, formatDateTime, formatRelativeTime, formatBirthDate, getInitials, cn } from "../lib/utils";
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Clock,
  MessageSquare,
  History,
  Edit,
  Trash2,
  Pin,
  PinOff,
  MoreHorizontal,
  Send,
  User,
  Tag,
  Globe,
  Loader2,
  Eye,
  EyeOff,
  Lock,
  Bookmark,
  BookmarkCheck,
  Megaphone,
  Package,
} from "lucide-react";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Separator } from "../components/ui/separator";
import { LeadFormDialog } from "../components/leads/LeadFormDialog";

export const LeadDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();

  const [lead, setLead] = useState(null);
  const [notes, setNotes] = useState([]);
  const [history, setHistory] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [noteVisibility, setNoteVisibility] = useState("VISIBLE_TO_SELLER");
  const [submittingNote, setSubmittingNote] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [editNoteContent, setEditNoteContent] = useState("");
  const [deleteNoteDialog, setDeleteNoteDialog] = useState(null);
  const [togglingMarker, setTogglingMarker] = useState(false);

  const fetchData = async () => {
    try {
      const [leadRes, notesRes, historyRes, statusesRes] = await Promise.all([
        getLead(id),
        getLeadNotes(id),
        getLeadHistory(id),
        getStatuses(),
      ]);
      setLead(leadRes.data);
      setNotes(notesRes.data);
      setHistory(historyRes.data);
      setStatuses(statusesRes.data);

      if (isAdmin) {
        const sellersRes = await getSellers();
        setSellers(sellersRes.data);
      }
    } catch (error) {
      toast.error("Erreur lors du chargement du lead");
      navigate("/leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleStatusChange = async (statusId) => {
    try {
      await updateLead(id, { statusId });
      toast.success("Statut mis à jour");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleSellerChange = async (sellerId) => {
    try {
      await updateLead(id, { assignedSellerId: sellerId || null });
      toast.success("Attribution mise à jour");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleToggleMarker = async () => {
    setTogglingMarker(true);
    try {
      const response = await toggleLeadMarker(id);
      setLead(prev => ({ ...prev, isMarked: response.data.isMarked }));
      toast.success(response.data.message);
    } catch (error) {
      toast.error("Erreur lors du marquage");
    } finally {
      setTogglingMarker(false);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    setSubmittingNote(true);
    try {
      await createLeadNote(id, {
        content: newNote,
        visibility: noteVisibility,
      });
      toast.success("Note ajoutée");
      setNewNote("");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'ajout de la note");
    } finally {
      setSubmittingNote(false);
    }
  };

  const handleUpdateNote = async () => {
    if (!editingNote || !editNoteContent.trim()) return;

    try {
      await updateLeadNote(id, editingNote, { content: editNoteContent });
      toast.success("Note modifiée");
      setEditingNote(null);
      setEditNoteContent("");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la modification");
    }
  };

  const handleDeleteNote = async () => {
    if (!deleteNoteDialog) return;

    try {
      await deleteLeadNote(id, deleteNoteDialog);
      toast.success("Note supprimée");
      setDeleteNoteDialog(null);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handlePinNote = async (noteId, isPinned) => {
    try {
      await updateLeadNote(id, noteId, { isPinned: !isPinned });
      toast.success(isPinned ? "Note désépinglée" : "Note épinglée");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const getVisibilityIcon = (visibility) => {
    switch (visibility) {
      case "ADMIN_ONLY":
        return <Lock className="h-3 w-3" />;
      case "PRIVATE_TO_AUTHOR":
        return <EyeOff className="h-3 w-3" />;
      default:
        return <Eye className="h-3 w-3" />;
    }
  };

  const getVisibilityLabel = (visibility) => {
    switch (visibility) {
      case "ADMIN_ONLY":
        return "Admin uniquement";
      case "PRIVATE_TO_AUTHOR":
        return "Privée";
      default:
        return "Visible";
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="lead-detail-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Skeleton className="h-64" />
            <Skeleton className="h-96" />
          </div>
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  if (!lead) return null;

  return (
    <div className="space-y-6" data-testid="lead-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            data-testid="back-button"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-4">
            <Avatar className="h-14 w-14">
              <AvatarFallback className="bg-primary text-primary-foreground text-xl font-bold">
                {getInitials(`${lead.firstName} ${lead.lastName}`)}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold tracking-tight">
                  {lead.firstName} {lead.lastName}
                </h1>
                {lead.isMarked && (
                  <BookmarkCheck className="h-5 w-5 text-orange-500" />
                )}
              </div>
              <div className="flex items-center gap-3 mt-1">
                <p className="text-muted-foreground text-sm">
                  Créé le {formatDate(lead.createdAt)}
                </p>
                {lead.campaignName && (
                  <Badge 
                    variant="outline" 
                    className="text-xs"
                    style={{ borderColor: lead.campaignColor, color: lead.campaignColor }}
                  >
                    <Megaphone className="h-3 w-3 mr-1" />
                    {lead.campaignName}
                  </Badge>
                )}
                {lead.sourceName && (
                  <Badge variant="secondary" className="text-xs">
                    <Package className="h-3 w-3 mr-1" />
                    {lead.sourceName}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant={lead.isMarked ? "default" : "outline"}
            size="icon"
            onClick={handleToggleMarker}
            disabled={togglingMarker}
            title={lead.isMarked ? "Retirer le marqueur" : "Marquer ce lead"}
            className={lead.isMarked ? "bg-orange-500 hover:bg-orange-600" : ""}
          >
            {togglingMarker ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : lead.isMarked ? (
              <BookmarkCheck className="h-4 w-4" />
            ) : (
              <Bookmark className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="outline"
            onClick={() => setEditDialogOpen(true)}
            data-testid="edit-lead-button"
          >
            <Edit className="h-4 w-4 mr-2" />
            Modifier
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Lead Info Card */}
          <Card data-testid="lead-info-card">
            <CardHeader className="pb-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <CardTitle className="text-lg">Informations</CardTitle>
                <div className="flex flex-wrap gap-2">
                  <Select
                    value={lead.statusId || ""}
                    onValueChange={handleStatusChange}
                  >
                    <SelectTrigger className="w-[180px]" data-testid="status-select">
                      <SelectValue placeholder="Statut">
                        {lead.statusName ? (
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: lead.statusColor }}
                            />
                            {lead.statusName}
                          </div>
                        ) : (
                          "Sélectionner un statut"
                        )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {statuses.map((status) => (
                        <SelectItem key={status.id} value={status.id}>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: status.color }}
                            />
                            {status.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {isAdmin && (
                    <Select
                      value={lead.assignedSellerId || "unassigned"}
                      onValueChange={(v) =>
                        handleSellerChange(v === "unassigned" ? null : v)
                      }
                    >
                      <SelectTrigger className="w-[180px]" data-testid="seller-select">
                        <SelectValue placeholder="Vendeur">
                          {lead.assignedSellerName || "Non attribué"}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Non attribué</SelectItem>
                        {sellers.map((seller) => (
                          <SelectItem key={seller.id} value={seller.id}>
                            {seller.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  {lead.email && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Mail className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-widest">
                          Email
                        </p>
                        <a
                          href={`mailto:${lead.email}`}
                          className="text-sm font-medium hover:text-primary"
                        >
                          {lead.email}
                        </a>
                      </div>
                    </div>
                  )}

                  {lead.phone && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Phone className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-widest">
                          Téléphone
                        </p>
                        <a
                          href={`tel:${lead.phone}`}
                          className="text-sm font-medium hover:text-primary"
                        >
                          {lead.phone}
                        </a>
                      </div>
                    </div>
                  )}

                  {lead.birthDate && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Calendar className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-widest">
                          Date de naissance
                        </p>
                        <p className="text-sm font-medium">{formatBirthDate(lead.birthDate)}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  {(lead.address || lead.city || lead.zip || lead.country) && (
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <MapPin className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-widest">
                          Adresse
                        </p>
                        <p className="text-sm font-medium">
                          {[lead.address, lead.zip, lead.city, lead.country]
                            .filter(Boolean)
                            .join(", ")}
                        </p>
                      </div>
                    </div>
                  )}

                  {lead.source && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Globe className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-widest">
                          Source
                        </p>
                        <p className="text-sm font-medium">{lead.source}</p>
                      </div>
                    </div>
                  )}

                  {lead.tags?.length > 0 && (
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Tag className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-widest">
                          Tags
                        </p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {lead.tags.map((tag, i) => (
                            <Badge key={i} variant="secondary">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {lead.lastContactedAt && (
                <div className="mt-6 pt-4 border-t flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  Dernier contact : {formatDateTime(lead.lastContactedAt)}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes & History Tabs */}
          <Tabs defaultValue="notes" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="notes" className="gap-2" data-testid="notes-tab">
                <MessageSquare className="h-4 w-4" />
                Notes ({notes.length})
              </TabsTrigger>
              <TabsTrigger value="history" className="gap-2" data-testid="history-tab">
                <History className="h-4 w-4" />
                Historique ({history.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="notes" className="mt-4">
              <Card>
                <CardContent className="p-4 space-y-4">
                  {/* Add Note Form */}
                  <form onSubmit={handleAddNote} className="space-y-3">
                    <Textarea
                      placeholder="Ajouter une note..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      rows={3}
                      data-testid="new-note-textarea"
                    />
                    <div className="flex items-center justify-between">
                      <Select value={noteVisibility} onValueChange={setNoteVisibility}>
                        <SelectTrigger className="w-[180px]" data-testid="note-visibility-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="VISIBLE_TO_SELLER">
                            <div className="flex items-center gap-2">
                              <Eye className="h-4 w-4" />
                              Visible pour tous
                            </div>
                          </SelectItem>
                          {isAdmin && (
                            <SelectItem value="ADMIN_ONLY">
                              <div className="flex items-center gap-2">
                                <Lock className="h-4 w-4" />
                                Admin uniquement
                              </div>
                            </SelectItem>
                          )}
                          <SelectItem value="PRIVATE_TO_AUTHOR">
                            <div className="flex items-center gap-2">
                              <EyeOff className="h-4 w-4" />
                              Privée
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <Button type="submit" disabled={!newNote.trim() || submittingNote} data-testid="submit-note-button">
                        {submittingNote ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Send className="h-4 w-4 mr-2" />
                            Envoyer
                          </>
                        )}
                      </Button>
                    </div>
                  </form>

                  <Separator />

                  {/* Notes List */}
                  {notes.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      Aucune note pour ce lead
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {notes.map((note) => (
                        <div
                          key={note.id}
                          className={cn(
                            "p-4 rounded-lg border",
                            note.isPinned && "bg-accent/5 border-accent/30"
                          )}
                          data-testid={`note-${note.id}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-3">
                              <Avatar className="h-8 w-8">
                                <AvatarFallback className="text-xs">
                                  {getInitials(note.authorName)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium text-sm">
                                    {note.authorName}
                                  </span>
                                  <Badge variant="outline" className="text-xs">
                                    {note.authorRole === "ADMIN" ? "Admin" : "Vendeur"}
                                  </Badge>
                                  <Badge
                                    variant="secondary"
                                    className="text-xs flex items-center gap-1"
                                  >
                                    {getVisibilityIcon(note.visibility)}
                                    {getVisibilityLabel(note.visibility)}
                                  </Badge>
                                  {note.isPinned && (
                                    <Badge className="text-xs bg-accent text-accent-foreground">
                                      <Pin className="h-3 w-3 mr-1" />
                                      Épinglée
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {formatDateTime(note.createdAt)}
                                  {note.updatedAt !== note.createdAt && " (modifié)"}
                                </p>
                              </div>
                            </div>

                            {(isAdmin || note.authorUserId === user?.id) && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {isAdmin && (
                                    <DropdownMenuItem
                                      onClick={() => handlePinNote(note.id, note.isPinned)}
                                    >
                                      {note.isPinned ? (
                                        <>
                                          <PinOff className="h-4 w-4 mr-2" />
                                          Désépingler
                                        </>
                                      ) : (
                                        <>
                                          <Pin className="h-4 w-4 mr-2" />
                                          Épingler
                                        </>
                                      )}
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setEditingNote(note.id);
                                      setEditNoteContent(note.content);
                                    }}
                                  >
                                    <Edit className="h-4 w-4 mr-2" />
                                    Modifier
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="text-destructive"
                                    onClick={() => setDeleteNoteDialog(note.id)}
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Supprimer
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>

                          {editingNote === note.id ? (
                            <div className="mt-3 space-y-2">
                              <Textarea
                                value={editNoteContent}
                                onChange={(e) => setEditNoteContent(e.target.value)}
                                rows={3}
                              />
                              <div className="flex gap-2">
                                <Button size="sm" onClick={handleUpdateNote}>
                                  Enregistrer
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setEditingNote(null);
                                    setEditNoteContent("");
                                  }}
                                >
                                  Annuler
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <p className="mt-3 text-sm whitespace-pre-wrap">{note.content}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="history" className="mt-4">
              <Card>
                <CardContent className="p-4">
                  {history.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      Aucun historique pour ce lead
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {history.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-start gap-4 py-3 border-b last:border-0"
                          data-testid={`history-${item.id}`}
                        >
                          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                            <History className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm">
                              <span className="font-medium">{item.changedByUserName}</span>
                              {" a changé le statut "}
                              {item.oldStatusName && (
                                <>
                                  de{" "}
                                  <Badge variant="outline">{item.oldStatusName}</Badge>
                                  {" "}
                                </>
                              )}
                              vers{" "}
                              <Badge variant="outline">{item.newStatusName}</Badge>
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatDateTime(item.changedAt)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-widest">
                Résumé
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Notes</span>
                <span className="font-medium">{notes.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Changements de statut</span>
                <span className="font-medium">{history.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Créé le</span>
                <span className="font-medium">{formatDate(lead.createdAt)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Mis à jour</span>
                <span className="font-medium">{formatRelativeTime(lead.updatedAt)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Assigned Seller */}
          {lead.assignedSellerName && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-widest">
                  Vendeur attribué
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      {getInitials(lead.assignedSellerName)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{lead.assignedSellerName}</p>
                    <p className="text-xs text-muted-foreground">Vendeur</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Edit Dialog */}
      <LeadFormDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        lead={lead}
        onSuccess={() => {
          setEditDialogOpen(false);
          fetchData();
        }}
        statuses={statuses}
        sellers={sellers}
      />

      {/* Delete Note Dialog */}
      <AlertDialog
        open={!!deleteNoteDialog}
        onOpenChange={() => setDeleteNoteDialog(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette note ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteNote}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
